'use client';

import { useStorageSignedUrl } from '@kit/supabase/hooks/use-storage-signed-url';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

/**
 * StorageImage component that displays images from private Supabase Storage buckets.
 *
 * Accepts either form:
 * - Raw storage path: "identity-documents/<uid>/.../file.png" — signs on demand.
 * - Already-signed URL: "https://.../object/sign/...?token=..." — used as-is.
 *
 * The two-shape input is intentional: the wizard sets the raw path right
 * after upload (handleFileUploadSuccess), and the server returns a signed
 * URL after page reload via getIdentityVerificationAction. parseStoragePath
 * (used by useStorageSignedUrl) splits on the first slash, which turns
 * "https://..." into bucket="https:" and silently fails. Detect URL input
 * here and skip the re-sign.
 */
export function StorageImage({
  storagePath,
  alt,
  className,
}: {
  storagePath: string;
  alt: string;
  className?: string;
}) {
  const isAlreadyUrl = /^https?:\/\//i.test(storagePath);
  const { url, loading, error } = useStorageSignedUrl(
    isAlreadyUrl ? null : storagePath,
  );
  const resolvedUrl = isAlreadyUrl ? storagePath : url;
  const isLoading = isAlreadyUrl ? false : loading;
  const hasError = isAlreadyUrl ? false : error;

  if (isLoading) {
    return (
      <div
        className={cn('bg-muted flex items-center justify-center', className)}
      >
        <div className="border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (hasError || !resolvedUrl) {
    return (
      <div
        className={cn(
          'bg-muted text-muted-foreground flex items-center justify-center',
          className,
        )}
      >
        <span className="text-xs">
          <Trans i18nKey="dancer:identityVerification.failedToLoadImage" />
        </span>
      </div>
    );
  }

  // eslint-disable-next-line @next/next/no-img-element -- Supabase signed URL (rotating token); image-rendering.md forbids next/image on media
  return <img src={resolvedUrl} alt={alt} className={className} />;
}
